
import { Vue, Component, Prop } from "vue-property-decorator";

import ModalFullScreen from "@/components/modal/ModalFullScreenNew.vue";
import CrossIcon from "@/components/icons/CrossIconNew.vue";

import UIButton from "@/components/button/ui-button.vue";
import Spinner from "@/components/common/Spinner.vue";
import { v4 as uuid } from "uuid";
import storage from "@/services/storage";
import splitFileNameExt from "@/utils/splitFileNameExt";
import { NOTIFICATION } from "@/entities/Events";
import Message from "@/data/notificationsMessages";
import CrossIconHover from "@/components/icons/CrossIconHover.vue";
import { FileSizeRestrictions } from "@/entities/FileSizeRestrictions";
import UPDATE_RESUME from "@/apollo/profile/resume/UpdateResume.gql";
import CompanyContractorDetailedInformation from "@/components/contractors/ContractorDetailedInformationResume.vue";
import AffindaClient from "@/services/resumeScraping/affinda";
import DOCIcon from "@/components/icons/DOCIcon.vue";
import PDFIcon from "@/components/icons/PDFIcon.vue";
import CREATE_USER_EXPERIENCE from "@/apollo/profile/experiences/CreateExperience.gql";
import {
  User_Education,
  User_Profile_By_Management,
} from "@/generated/graphql";
import UPSERT_PROFILE_BY_MANAGEMENT from "@/apollo/profile/profileDetails/management/UpsertProfileDetailsByManagement.gql";
import CREATE_USER_EDUCATION from "@/apollo/profile/educations/CreateEducation.gql";
import GET_AFFINDA_PROFILE from "@/apollo/profile/affinda/GetAffindaProfile.gql";
import DELETE_AFFINDA_PROFILE from "@/apollo/profile/affinda/DeleteAffindaProfile.gql";
import UserResume from "@/entities/UserResume";

interface HTMLInputEvent extends Event {
  target: HTMLInputElement & EventTarget;
}

@Component({
  components: {
    ModalFullScreen,
    CompanyContractorDetailedInformation,
    UIButton,
    CrossIcon,
    CrossIconHover,
    DOCIcon,
    PDFIcon,
    Spinner,
  },
})
export default class AddResumeModal extends Vue {
  @Prop({ type: String, required: true }) readonly userId!: string;
  @Prop({ type: String, required: true }) readonly contractorId!: string;
  @Prop({ type: String, required: true }) readonly contractorUserId!: string;

  hasFile: boolean = false;
  currentModal: 0;
  file: File | null = null;
  fileName: string = "";
  fileSize: string = "";
  showResumeModal: boolean = false;
  fileError: boolean = false;
  fileErrorName: string = "";
  processing: boolean = false;
  resume: string = "";
  showFile: boolean = true;
  resumeData: any = {};
  isHovered: boolean = false;
  affindaClient: AffindaClient = new AffindaClient();

  validateFileTypes(file) {
    const allowedTypes = [
      "application/msword", // DOC
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // DOCX
      "application/pdf", // PDF
    ];

    if (!allowedTypes.includes(file.type)) {
      this.$eventBus.emit(
        NOTIFICATION,
        Message.errorMessage(
          `Invalid file format. Please upload a PDF or DOC file.`
        )
      );
      this.fileErrorName =
        "Invalid file format. Please upload a PDF or DOC file.";
      this.fileError = true;
      this.processing = false;
      return false;
    } else {
      this.fileErrorName = "";
      this.fileError = false;
      return true;
    }
  }

  validateFileSize(file) {
    const filesize = file.size / 1024 / 1024;
    if (filesize > FileSizeRestrictions.DOCUMENT_MAX) {
      this.$eventBus.emit(
        NOTIFICATION,
        Message.errorMessage(
          `The maximum allowed file size is ${FileSizeRestrictions.DOCUMENT_MAX} MB`
        )
      );
      this.processing = false;
      this.fileErrorName = "File too big please keep files under 10MB size.";
      this.fileError = true;
      return false;
    } else {
      this.fileErrorName = "";
      this.fileError = false;
      return true;
    }
  }

  get removeFile() {
    return (this.hasFile = false);
  }
  handleFileUpload(): void {
    (this.$refs.fileUpload as HTMLInputElement).click();
  }

  async changeFiles(event: HTMLInputEvent): Promise<void> {
    this.processing = true;
    const files = event.target.files;

    if (!files) {
      return;
    }

    const file = files[0];
    this.fileName = file.name;
    this.fileSize = `${file.size} KB`;

    // Validate file size
    if (!this.validateFileSize(file)) {
      return;
    }
    // validate file type
    if (!this.validateFileTypes(file)) {
      return;
    }

    // Add file to Affinda and get the parsed data
    const data = await this.affindaClient.createResume(file);

    if (data) {
      this.hasFile = true;
      this.processing = false;
      this.resumeData = data.data;
    }
    // Emit event to update the user resume

    this.$eventBus.emit(UserResume.UPDATE_RESUME, data);

    // Upload the file to S3
    await this.uploadFile(file);
  }

  addFileUpload() {
    this.processing = true;
    // this.upsertProfileByManagement();
    this.processing = false;
    this.showResumeModal = true;
  }
  async handleRemoveFile(): Promise<void> {
    // [qtrinh@ideatheorem.com] We also need to check and then delete if docuemnt exists
    const userAffindaProfileResponse = await this.$apollo.query({
      query: GET_AFFINDA_PROFILE,
      variables: {
        user_id: this.userId,
      },
    });

    if (userAffindaProfileResponse.data.user_affinda_profile?.length >= 0) {
      await this.$apollo.mutate({
        mutation: DELETE_AFFINDA_PROFILE,
        variables: {
          user_id: this.userId,
        },
      });
      const userAffindaProfile =
        userAffindaProfileResponse.data.user_affinda_profile[0];
      await this.affindaClient.deleteDocument(userAffindaProfile?.document_id);
    }

    await storage.delete(this.resume);
    await this.updateResume(null);
  }

  async uploadFile(file: File): Promise<void> {
    try {
      const { ext, name } = splitFileNameExt(file.name);
      const s3Filename = `${name}-${uuid()}.${ext}`;

      await storage.save(s3Filename, file);
      if (this.resume) {
        await storage.delete(this.resume);
      }

      await this.updateResume(s3Filename);
    } catch (_) {}
  }

  async updateResume(resume: string | null): Promise<void> {
    try {
      await this.update(resume);

      const purpose = Boolean(resume) ? "updated" : "deleted";
      this.$eventBus.emit(
        NOTIFICATION,
        Message.successMessage(`Your resume had been successfully ${purpose}.`)
      );

      // this.refetch();
    } catch (e) {
      this.$eventBus.emit(NOTIFICATION, Message.errorMessage(e.message));
    }
  }

  async update(resume: string | null): Promise<void> {
    await this.$apollo.mutate({
      mutation: UPDATE_RESUME,
      variables: {
        userId: this.userId,
        resume,
      },
    });
  }

  refetch(): void {
    this.$apollo.queries.resume.refetch();
    this.$emit("update");
  }

  async upsertProfileByManagement(): Promise<void> {
    this.processing = true;
    let profileByManagementSet: Partial<User_Profile_By_Management> = {
      user_id: this.contractorUserId,
      added_by_id: this.userId,
      email: this.resumeData ? this.resumeData.emails[0] : "",
      phone: this.resumeData ? this.resumeData.phoneNumbers[0] : undefined,
      city:
        this.resumeData && this.resumeData.location
          ? this.resumeData.location.city
          : "",
      country:
        this.resumeData && this.resumeData.location
          ? this.resumeData.location.country
          : "",
      state:
        this.resumeData && this.resumeData.location
          ? this.resumeData.location.state
          : "",
      occupation: this.resumeData ? this.resumeData.profession : "",
      postal_code: this.resumeData ? this.resumeData.postal_code : "",
      updated_at: this.resumeData ? this.resumeData.updated_at : "",
      website: this.resumeData ? this.resumeData.websites[0] : "",
      year_of_experience: this.resumeData
        ? this.resumeData.totalYearsExperience
        : 0,
    };
    await this.$apollo.mutate({
      mutation: UPSERT_PROFILE_BY_MANAGEMENT,
      variables: {
        userId: this.contractorUserId,
        added_by_id: this.userId,
        profileByManagementSet: profileByManagementSet,
      },
    });
    await this.upsertEducation();
    await this.upsertExperience();
    // await this.upsertLanguages();
    // await this.upsertSkills();
    this.processing = false;
    // this.$emit("refetchProfileByManagement");
    // this.$emit("close");
  }

  get educationsByManagement() {
    const educations = [];
    const scrappedResume = this.resumeData;
    if (
      scrappedResume &&
      scrappedResume.hasOwnProperty("education") &&
      scrappedResume.education &&
      scrappedResume.education.length > 0
    ) {
      scrappedResume.education.map(async (edu) => {
        const educationId = uuid();
        let education: User_Education = {
          id: educationId,
          user_id: this.contractorUserId,
          title: edu.accreditation?.education,
          institution: edu.organization,
          start_date: edu.dates?.startDate,
          end_date: edu.dates?.completionDate,
          summary: edu.summary,
          added_by_id: this.userId,
          user: null,
        };
        educations.push(education);
      });
    }
    return educations;
  }

  get experiencesByManagement() {
    const experiences = [];
    const scrappedResume = this.resumeData;
  
    if (
      scrappedResume &&
      scrappedResume.hasOwnProperty("workExperience") &&
      scrappedResume.workExperience &&
      scrappedResume.workExperience.length > 0
    ) {
      const workExperience = scrappedResume.workExperience;
      for (const exp of workExperience) {
        let experience = {
          user_id: this.contractorUserId,
          job_title: exp.jobTitle,
          company_name: exp.organization,
          employment_type: "",
          ongoing: !exp.dates?.endDate ? true : false,
          location: exp.location ? exp.location.rawInput : "",
          summary: exp.jobDescription,
          start_date: exp.dates?.startDate,
          end_date: exp.dates?.endDate,
          industry_id: null,
          added_by_id: this.userId,
          created_at: new Date(),
          skills: {data: []},
          user: null,
        };
        experiences.push(experience);
      }
    }
    return experiences;
  }

  async upsertEducation(): Promise<void> {
    await Promise.all(
      this.educationsByManagement.map(async (educationItem) => {
        await this.$apollo.mutate({
          mutation: CREATE_USER_EDUCATION,
          variables: {
            educationSet: educationItem,
          },
        });
      })
    );
  }

  async upsertExperience(): Promise<void> {
    await Promise.all(
      this.experiencesByManagement.map(async (experItem) => {
        await this.$apollo.mutate({
          mutation: CREATE_USER_EXPERIENCE,
          variables: {
            experienceSet: experItem,
          },
        });
      })
    );
  }
}
